import { cloneDeep } from 'lodash';
import { observable } from 'mobx';
import * as Enums from './Enums';
import { store } from './Store';
import { userPermissionScopeOrder } from './Enums';

// The type for User Permissions
export class UserPermissions {
	@observable public commonManageOrganisations?: Enums.organisationPermissionScope;
	@observable public commonViewOrganisations?: Enums.organisationPermissionScope;
	@observable public commonManageBusinessEntities?: Enums.businessEntitiesPermissionScope;
	@observable public commonViewBusinessEntities?: Enums.businessEntitiesPermissionScope;
	@observable public commonManageUsers?: Enums.userPermissionScope;
	@observable public commonViewUsers?: Enums.userPermissionScope;
	@observable public commonManageReferrals?: boolean;

	@observable public intelUploadAtb?: boolean;
	@observable public intelCustomerOverview?: boolean;
	@observable public intelManageCustomerGroups?: boolean;
	@observable public intelCreateCustomerGroups?: boolean;
	@observable public intelManageCustomerBusinessInformation?: boolean;
	@observable public intelManageCustomerAccountsInformation?: boolean;
	@observable public intelViewCustomerPpsrRegistrations?: boolean;

	@observable public approveReferCustomerApplication?: boolean;
	@observable public approveAssessCustomerApplication?: boolean;
	@observable public approveProcessPpsr?: boolean;
	@observable public approveViewCustomerApplicationEmails?: boolean;

	@observable public ppsrRegister?: boolean;
	@observable public ppsrRenew?: boolean;
	@observable public ppsrDischarge?: boolean;
	@observable public ppsrAmendmentsMinor?: boolean;
	@observable public ppsrAmendmentsMajor?: boolean;
	@observable public ppsrReissueVerificationStatements?: boolean;
	@observable public ppsrView?: boolean;
	@observable public ppsrSearch?: boolean;
	@observable public ppsrViewMySearches?: boolean;
	@observable public ppsrDownloadRegistrations?: boolean;
	@observable public ppsrManageSpg?: boolean;
	@observable public ppsrProcessBulkUpload?: boolean;
	@observable public ppsrViewAssignedProfiles?: boolean;

	// Ensure that typename is not stored
	constructor(attributes?: {[key: string]: any}) {
		if (attributes) {
			Object.keys(attributes).forEach(key => {
				if (key !== '__typename') {
					this[key] = attributes[key];
				}
			});
		}
	}
}


/**
 * Returns the default permissions for a given user type
 * @param userType The userType for the default permissions
 * @returns The default permissions for the given userType
 */
export function defaultPermissionsForUserType(userType: Enums.userType): UserPermissions {
	return cloneDeep(store.defaultUserPermissions[userType]);
}

/**
 * Returns the calculated permissions for a user given their userType and what permissions they have overriden.
 * @param userType The userType for the permissions we are calculating
 * @param permissionOverrides The permissions that have been overriden
 * @returns The calculated permissions a user has given their usertype and the overriden permissions
 */
export function calculateUserPermissions(userType: Enums.userType, permissionOverrides?: UserPermissions) : UserPermissions {
	const userTypePermissions = defaultPermissionsForUserType(userType);

	// If they have no overriden permissions, return the default for the given userType
	if (!permissionOverrides) return userTypePermissions;

	// loop through all the permissions and calculate the necessary value
	for (const key in userTypePermissions) {
		if (permissionOverrides[key] !== undefined && permissionOverrides[key] !== null) {
			userTypePermissions[key] = permissionOverrides[key];
		}
	}

	return userTypePermissions;
}

/**
 * Returns the calculated permissions for a user given their userType and what permissions they have overriden.
 * @param userType The userType for the permissions we are calculating
 * @returns The calculated permissions a user has given their usertype and the overriden permissions
 */
export function calculateOtherUserTypeVisiblePermissions(userType: Enums.userType): UserPermissions {
	const userTypePermissions = defaultPermissionsForUserType(userType);

	// loop through all the permissions and ensure that the permission does not exceed the controlling user
	for (const key in userTypePermissions[userType]) {
		if (!store.userPermissions[key]) {
			switch (typeof (store.userPermissions[key])) {
				case 'boolean':
					userTypePermissions[key] = false;
					break;
				case 'string':
					userTypePermissions[key] = 'SELF';
			}
		} else if (typeof (userTypePermissions[key]) === 'string') {
			if (userPermissionScopeOrder[userTypePermissions[key]] > userPermissionScopeOrder[store.userPermissions[key]]) {
				userTypePermissions[key] = store.userPermissions[key];
			}
		}
	}

	return userTypePermissions;
}
